import {
  ButtonGroup,
  ButtonProps,
  TabProps,
  Tabs,
  TextInput,
  closeCurrentDialog,
} from 'platform/components';
import {Box, Grid, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {head, isNil, isNotNil} from 'ramda';

import {RecordInformationBody} from '@dms/api/core';
import {RecordIdHttpModel} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {DmsDataGrid} from '@dms/shared';

import {buildArray, getSafely} from 'shared/web';

import {hasRecordInformation} from '../utils/hasRecordInformation';
import {
  RECORD_AUDIT_PAIR_GRID_CODES,
  RECORD_AUDIT_PAIR_RESOURCE_IDS,
  RecordAuditPairResourceId,
  getRecordAuditPairResourceLabel,
  isRecordAuditPairResourceId,
} from './recordAuditPairResources';

const CUSTOM_TAB_ID = 'CUSTOM';

type ActiveTabId = RecordAuditPairResourceId | typeof CUSTOM_TAB_ID;

const EMPTY_CUSTOM_INFO: RecordInformationBody = {
  regPlate: null,
  vin: null,
  orderNumber: null,
  description: null,
};

interface RecordAuditPairRecordDialogProps {
  initialResourceId?: RecordAuditPairResourceId;
  initialRecordInformation?: RecordInformationBody | null;
  onConfirm: (record: RecordIdHttpModel) => void;
  onConfirmCustom: (recordInformation: RecordInformationBody) => void;
  onClear?: () => void;
}

export function RecordAuditPairRecordDialog(props: RecordAuditPairRecordDialogProps) {
  const initialTabId: ActiveTabId =
    props.initialResourceId ??
    (isNotNil(props.initialRecordInformation) ? CUSTOM_TAB_ID : RECORD_AUDIT_PAIR_RESOURCE_IDS[0]);
  const [activeTabId, setActiveTabId] = useState<ActiveTabId>(initialTabId);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [customInfo, setCustomInfo] = useState<RecordInformationBody>({
    ...EMPTY_CUSTOM_INFO,
    ...props.initialRecordInformation,
  });

  const handleTabChange = (id: string) => {
    if (id !== CUSTOM_TAB_ID && !isRecordAuditPairResourceId(id)) {
      return;
    }
    setActiveTabId(id);
    setSelectedRowId(null);
  };

  const handleConfirm = () => {
    match(activeTabId)
      .with(CUSTOM_TAB_ID, () => {
        props.onConfirmCustom(customInfo);
        closeCurrentDialog();
      })
      .otherwise((resourceId) => {
        if (isNil(selectedRowId)) {
          return;
        }
        props.onConfirm({resourceId, id: selectedRowId});
        closeCurrentDialog();
      });
  };

  const tabs: TabProps[] = [
    ...RECORD_AUDIT_PAIR_RESOURCE_IDS.map((id) => ({
      id,
      title: getRecordAuditPairResourceLabel(id),
      content: (
        <Box height={150}>
          <DmsDataGrid
            gridCode={getSafely(RECORD_AUDIT_PAIR_GRID_CODES, id) ?? ''}
            onRowSelectionChange={(rows) => setSelectedRowId(head(rows)?.id ?? null)}
          />
        </Box>
      ),
    })),
    {
      id: CUSTOM_TAB_ID,
      title: i18n.t('page.checklistAudits.pairing.tabs.custom'),
      content: (
        <Box paddingTop={2}>
          <Grid columns={[1, 2]}>
            <TextInput
              label={i18n.t('page.checklistAudits.create.fields.regPlate')}
              value={customInfo.regPlate ?? null}
              onChange={(value) => setCustomInfo((prev) => ({...prev, regPlate: value}))}
            />
            <TextInput
              label={i18n.t('page.checklistAudits.create.fields.vin')}
              value={customInfo.vin ?? null}
              onChange={(value) => setCustomInfo((prev) => ({...prev, vin: value}))}
            />
            <TextInput
              label={i18n.t('page.checklistAudits.create.fields.orderNumber')}
              value={customInfo.orderNumber ?? null}
              onChange={(value) => setCustomInfo((prev) => ({...prev, orderNumber: value}))}
            />
            <TextInput
              label={i18n.t('page.checklistAudits.create.fields.recordDescription')}
              value={customInfo.description ?? null}
              onChange={(value) => setCustomInfo((prev) => ({...prev, description: value}))}
            />
          </Grid>
        </Box>
      ),
    },
  ];

  const handleClear = () => {
    props.onClear?.();
    closeCurrentDialog();
  };

  const isCustomActive = activeTabId === CUSTOM_TAB_ID;
  const isSaveDisabled = isCustomActive ? !hasRecordInformation(customInfo) : isNil(selectedRowId);
  const isPaired =
    isNotNil(props.initialResourceId) || hasRecordInformation(props.initialRecordInformation);

  return (
    <VStack spacing={4}>
      <Tabs
        variant="condensed"
        tabs={tabs}
        activeTabId={activeTabId}
        onChange={handleTabChange}
        lazyBehavior="unmount"
        isLazy
      />
      <ButtonGroup
        align="right"
        buttons={buildArray<ButtonProps>()
          .when(isPaired && isNotNil(props.onClear), {
            title: i18n.t('page.checklistAudits.pairing.clearCta'),
            onClick: handleClear,
            variant: 'ghostLink',
          })
          .add({
            title: i18n.t('general.actions.cancel'),
            onClick: closeCurrentDialog,
            variant: 'secondary',
          })
          .add({
            title: i18n.t('general.actions.save'),
            onClick: handleConfirm,
            isDisabled: isSaveDisabled,
          })}
      />
    </VStack>
  );
}
